import { createReducer, on } from '@ngrx/store';
import * as FboLocationsActions from './fbo-locations.actions';
import { UserFboLocationsDto } from 'src/app/api/administration/models/user-fbo-locations-dto';
import { FboLocationDto } from 'src/app/api/administration/models/fbo-location-dto';

export interface FboLocationsState {
  locations: UserFboLocationsDto[];
  selectedLocation: UserFboLocationsDto | null;
  isLoading: boolean;
  isSwitching: boolean;
  error: string | null;
  allLocations: UserFboLocationsDto[];
  totalRecords: number;
  isLoadingAll: boolean;
  errorAll: string | null;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  selectedLocationDetails: FboLocationDto | null;
  isLoadingDetails: boolean;
  errorDetails: string | null;
}

export const initialState: FboLocationsState = {
  locations: [],
  selectedLocation: null,
  isLoading: false,
  isSwitching: false,
  error: null,
  allLocations: [],
  totalRecords: 0,
  isLoadingAll: false,
  errorAll: null,
  isCreating: false,
  isUpdating: false,
  isDeleting: false,
  selectedLocationDetails: null,
  isLoadingDetails: false,
  errorDetails: null,
};

export const fboLocationsReducer = createReducer(
  initialState,
  // Load FBO Locations
  on(FboLocationsActions.loadFboLocations, (state) => ({
    ...state,
    isLoading: true,
    error: null,
  })),
  on(FboLocationsActions.loadFboLocationsSuccess, (state, { locations }) => {
    const activeLocation = locations.find((loc) => loc.active);
    return {
      ...state,
      locations,
      selectedLocation: activeLocation || locations[0] || null,
      isLoading: false,
      error: null,
    };
  }),
  on(FboLocationsActions.loadFboLocationsFailure, (state, { error }) => ({
    ...state,
    isLoading: false,
    error,
  })),
  // Switch FBO Location
  on(FboLocationsActions.switchFboLocation, (state) => ({
    ...state,
    isSwitching: true,
    error: null,
  })),
  on(FboLocationsActions.switchFboLocationSuccess, (state, { selectedLocation }) => {
    // Update the active flag for all locations
    const updatedLocations = state.locations.map((loc) => ({
      ...loc,
      active: loc.id === selectedLocation.id,
    }));
    return {
      ...state,
      locations: updatedLocations,
      selectedLocation,
      isSwitching: false,
      error: null,
    };
  }),
  on(FboLocationsActions.switchFboLocationFailure, (state, { error }) => ({
    ...state,
    isSwitching: false,
    error,
  })),
  // Reset State
  on(FboLocationsActions.resetFboLocationsState, () => initialState),
  // Load All FBO Locations
  on(FboLocationsActions.loadAllFboLocations, (state) => ({
    ...state,
    isLoadingAll: true,
    errorAll: null,
  })),
  on(FboLocationsActions.loadAllFboLocationsSuccess, (state, { locations, totalRecords }) => ({
    ...state,
    allLocations: locations,
    totalRecords,
    isLoadingAll: false,
    errorAll: null,
  })),
  on(FboLocationsActions.loadAllFboLocationsFailure, (state, { error }) => ({
    ...state,
    isLoadingAll: false,
    errorAll: error,
  })),
  // Create FBO Location
  on(FboLocationsActions.createFboLocation, (state) => ({
    ...state,
    isCreating: true,
    errorAll: null,
  })),
  on(FboLocationsActions.createFboLocationSuccess, (state, { location }) => ({
    ...state,
    allLocations: [...state.allLocations, location],
    totalRecords: state.totalRecords + 1,
    isCreating: false,
    errorAll: null,
  })),
  on(FboLocationsActions.createFboLocationFailure, (state, { error }) => ({
    ...state,
    isCreating: false,
    errorAll: error,
  })),
  // Get FBO Location Details
  on(FboLocationsActions.getFboLocationDetails, (state) => ({
    ...state,
    isLoadingDetails: true,
    errorDetails: null,
  })),
  on(FboLocationsActions.getFboLocationDetailsSuccess, (state, { location }) => ({
    ...state,
    selectedLocationDetails: location,
    isLoadingDetails: false,
    errorDetails: null,
  })),
  on(FboLocationsActions.getFboLocationDetailsFailure, (state, { error }) => ({
    ...state,
    isLoadingDetails: false,
    errorDetails: error,
  })),
  // Update FBO Location
  on(FboLocationsActions.updateFboLocation, (state) => ({
    ...state,
    isUpdating: true,
    errorAll: null,
  })),
  on(FboLocationsActions.updateFboLocationSuccess, (state, { location }) => ({
    ...state,
    allLocations: state.allLocations.map((loc) => (loc.id === location.id ? location : loc)),
    isUpdating: false,
    errorAll: null,
  })),
  on(FboLocationsActions.updateFboLocationFailure, (state, { error }) => ({
    ...state,
    isUpdating: false,
    errorAll: error,
  })),
  // Delete FBO Location
  on(FboLocationsActions.deleteFboLocation, (state) => ({
    ...state,
    isDeleting: true,
    errorAll: null,
  })),
  on(FboLocationsActions.deleteFboLocationSuccess, (state, { locationId }) => ({
    ...state,
    allLocations: state.allLocations.filter((loc) => loc.id !== locationId),
    isDeleting: false,
    errorAll: null,
  })),
  on(FboLocationsActions.deleteFboLocationFailure, (state, { error }) => ({
    ...state,
    isDeleting: false,
    errorAll: error,
  }))
);
